
import { useState, useEffect } from "react";
import ResourcePersonGuide from "../assets/resourse-person.jpeg";

// ── Icons ─────────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M5 12l7 7M5 12l7-7"/>
    </svg>
  );
}


const ACCENT_MAP = {
  default: "border-gray-100 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02]",
  indigo:  "border-indigo-100 dark:border-indigo-700/25 bg-indigo-50/50 dark:bg-indigo-950/20",
  rose:    "border-rose-100 dark:border-rose-700/25 bg-rose-50/50 dark:bg-rose-950/20",
  emerald: "border-emerald-100 dark:border-emerald-700/25 bg-emerald-50/30 dark:bg-emerald-950/10",
  amber:   "border-amber-100 dark:border-amber-700/25 bg-amber-50/50 dark:bg-amber-950/10",
  sky:     "border-sky-100 dark:border-sky-700/25 bg-sky-50/50 dark:bg-sky-950/10",
  violet:  "border-violet-100 dark:border-violet-700/25 bg-violet-50/50 dark:bg-violet-950/10",
  teal:    "border-teal-100 dark:border-teal-700/25 bg-teal-50/30 dark:bg-teal-950/10",
};

function Section({ accent = "default", title, children }) {
  return (
    <div className={`rounded-2xl border px-2 py-4 ${ACCENT_MAP[accent]}`}>
      <p className=" font-bold text-base text-gray-800  mb-3">{title}</p>
      {children}
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1.5 mt-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-[15px] leading-[1.8] text-gray-600 dark:text-gray-300">
          <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-black flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function NumberedList({ items }) {
  return (
    <ol className="space-y-1.5 mt-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-[15px] leading-[1.8] text-gray-600 dark:text-gray-300">
          <span className="mt-1 w-5 h-5 rounded-full bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-hover
                           text-xs font-bold flex items-center justify-center flex-shrink-0">
            {i + 1}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

function SubHeading({ children }) {
  return (
    <p className="font-semibold text-[14px] text-gray-700 dark:text-gray-200 mt-4 mb-1">{children}</p>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ResourcePersonRegistrationGuide() {
  const [agreed, setAgreed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900">

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur-lg">
        <div className="max-w-2xl  mx-auto px-2 md:px-5 h-14 flex items-center justify-end">

          {/* Back to dashboard */}
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-sm font-medium text-gray-500
                       hover:text-gray-900 transition-colors group"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200
                     bg-white dark:bg-white/5 group-hover:border-primary
                             group-hover:text-primary transition-all">
              <ArrowLeftIcon />
            </span>
            <span className="hidden sm:inline">Back to Dashboard</span>
          </button>

        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 md:px-5 py-14 pb-24 border">
        {visible && (
          <>
            {/* ── Title ── */}
            <div className="mb-3">
              <h1 className="text-black text-4xl max-md:text-lg font-bold ">
                Registration Instruction / Guidelines for ResourcePerson
              </h1>
            </div>

            {/* ── Intro image card ── */}
            <div className="mb-10 mt-6 rounded-2xl border border-dashed overflow-hidden">
              <img src={ResourcePersonGuide} alt="ResourcePerson guide" className="w-full h-56 object-cover object-top" />
            </div>

            {/* ── Body ── */}
            <div className="space-y-6 text-[15px] leading-[1.8] text-black">

              <p>
                This page explains the instructions, guidelines, and authentication requirements before you proceed.{" "}
                <strong className="text-black dark:text-white font-semibold">Fillop Tech Limited</strong> provides a platform
                where educators, professionals, and content creators can share knowledge, publish educational materials,
                and earn income by teaching learners around the world. Before proceeding to registration as a ResourcePerson or
                resource person, please carefully read the following instructions and guidelines.
              </p>
       


              <Section accent="default" title="1. Eligibility to Become a ResourcePerson">
                <p>ResourcePersons on Fillop must:</p>
                <BulletList items={[
                  "Possess knowledge / professional experience in the subject or course you intend to teach.",
                  "Be capable of creating original educational contents such as video lessons, notes, presentations, quizzes, or other learning resources.",
                  "Ensure that all materials you upload are your own work or that you have permission to use them.",
                  "Provide accurate personal information during registration.",
                ]} />
                <p className="mt-3 text-sm text-black dark:text-black italic">
                  Fillop reserves the right to review ResourcePerson applications and approve or reject accounts that do not meet our standards.
                </p>
              </Section>


              <Section accent="" title="2. Course and Educational Material Requirements">
                <p>ResourcePersons are responsible for ensuring that all content uploaded to the platform:</p>
                <BulletList items={[
                  "Is clear, educational, and valuable to learners.",
                  "Is well organised and professionally presented.",
                  "Does not violate copyright laws or intellectual property rights.",
                  "Does not contain offensive, misleading, or inappropriate content.",
                ]} />
                <SubHeading>Accepted content types include:</SubHeading>
                <BulletList items={[
                  "Video lectures",
                  "PDF materials",
                  "Slide presentations",
                  "Assignments and quizzes",
                  "Practice questions",
                  "Other educational resources",
                ]} />
                <p className="mt-3 text-sm text-black dark:text-black italic">
                  Fillop may review and approve courses before they are published on the platform.
                </p>
              </Section>

              <Section accent="" title="3. Content Ownership and Responsibility">
                <p className="mb-3">
                  ResourcePersons maintain ownership of their original educational materials. However, by uploading content to
                  Fillop, you grant the platform the right to host, display, and distribute the materials to enrolled students.
                </p>
                <p className="mb-3">
                  ResourcePersons are <strong className="text-black dark:text-gray-100 font-semibold">fully responsible</strong> for
                  the accuracy, legality, and quality of the materials they publish.
                </p>
                <p className="text-sm text-rose-500 dark:text-rose-400 font-medium">
                  Any violation of copyright or misuse of intellectual property may result in removal of the content or suspension of the ResourcePerson's account.
                </p>
              </Section>

              <Section accent="emerald" title="4. Earnings and Payments">
                <p className="mb-3">ResourcePersons can earn income from:</p>
                <BulletList items={[
                  "Paid courses",
                  "Educational materials",
                  "Other approved learning resources",
                ]} />
                <p className="mt-3 mb-2">
                  Payments to ResourcePersons will be processed according to FillopTech's{" "}
                  <strong className="text-gray-800 dark:text-gray-100 font-semibold">revenue-sharing policy</strong>.
                  ResourcePersons will have access to a dashboard where they can track:
                </p>
                <BulletList items={[
                  "Course enrollments",
                  "Sales",
                  "Earnings",
                  "Withdrawal requests",
                ]} />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 italic">
                  Payment schedules and conditions will be communicated within the ResourcePerson dashboard.
                </p>
              </Section>

              <Section accent="" title="5. Account Security">
                <p className="mb-3">After completing the registration process:</p>
                <NumberedList items={[
                  "You will receive your login details in your registered email address.",
                  "You will be required to change the default password immediately.",
                  "You must keep your login credentials confidential and secure.",
                ]} />
                <p className="mt-3 text-sm font-medium text-black dark:text-white">
                  ResourcePersons are responsible for any activities that occur under their account.
                </p>
              </Section>

              <Section accent="" title="6. ResourcePerson Responsibilities">
                <p className="mb-3">
                  ResourcePersons on Fillop are expected to actively support students throughout their learning journey
                  by performing the following responsibilities:
                </p>
                <SubHeading>Student Assessment</SubHeading>
                <p>
                  ResourcePersons must evaluate and grade student submissions, quizzes, or assignments after each course module
                  where assessment is required. This helps students understand their progress and areas that need improvement.
                </p>
                <SubHeading>Student Communication</SubHeading>
                <p>
                  ResourcePersons are expected to respond to questions and chat requests from enrolled students in a{" "}
                  <strong className="text-gray-800 dark:text-gray-100 font-semibold">timely and professional manner</strong>.
                  Active communication helps students stay engaged and improves the overall learning experience.
                </p>
                <SubHeading>Learning Support</SubHeading>
                <p>
                  ResourcePersons should provide guidance, clarification, and helpful feedback to students when necessary to ensure
                  they fully understand the course material.
                </p>
              </Section>


              <Section accent="teal" title="7. Course Description and Introduction">
                <p className="mb-3">
                  Before publishing a course on Fillop, ResourcePersons are required to provide a clear and informative course
                  introduction that will be displayed to students before they decide to enroll.
                </p>
                <p className="mb-2">The course introduction should include the following:</p>
                <SubHeading>Course Overview</SubHeading>
                <p>A brief explanation of what the course is about and the knowledge or skills students will gain.</p>
                <SubHeading>Who the Course is For</SubHeading>
                <p className="mb-1">ResourcePersons must clearly state the target audience, for example:</p>
                <BulletList items={[
                  "Beginners",
                  "Intermediate learners",
                  "Professionals",
                  "Students preparing for exams",
                ]} />
                <SubHeading>Course Expectations</SubHeading>
                <p>
                  ResourcePersons should explain what students will learn, what activities they will complete, and what outcomes
                  they can expect after finishing the course.
                </p>
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 italic">
                  This information helps students determine whether the course meets their learning needs before subscribing.
                </p>
              </Section>

              <Section accent="" title="8. Platform Conduct">
                <p className="mb-3">
                  All ResourcePersons must maintain{" "}
                  <strong className="text-gray-800 dark:text-gray-100 font-semibold">professional behaviour</strong>{" "}
                  while using the platform. The following actions are strictly prohibited:
                </p>
                <BulletList items={[
                  "Uploading plagiarised content",
                  "Posting false or misleading information",
                  "Harassing or abusing students",
                ]} />
                <p className="mt-3 text-sm text-rose-500 dark:text-rose-400 font-medium">
                  Violation of these rules may lead to account suspension or permanent removal from the platform.
                </p>
              </Section>

              <Section accent="default" title="9. Review and Approval Process">
                <p className="mb-3">To maintain quality learning standards, FillopTech may review:</p>
                <BulletList items={[
                  "ResourcePerson accounts",
                  "Course content",
                  "Educational materials",
                ]} />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 italic">
                  Courses may be edited, rejected, or removed if they do not meet the platform's standards.
                </p>
              </Section>


              <p className="text-base text-gray-500 dark:text-gray-400 italic">
                We look forward to having you as part of the FillopTech learning community. By authenticating this page
                and continuing with registration, you agree to comply with these guidelines.
              </p>
              <p className="font-bold text-gray-800 dark:text-gray-100">Welcome to FillopTech!</p>

            </div>{/* end body */}


            {/* ── Agreement + CTA ── */}
            <div className="mt-10 text-center">

              {/* Single div, onClick toggles — no hidden input, no flash */}
              <div
                className="inline-flex items-start gap-3 cursor-pointer mb-7 max-w-sm text-left select-none"
                onClick={() => setAgreed(a => !a)}
              >
                <div
                  className={`w-5 h-5 mt-0.5 rounded-md border flex-shrink-0 flex items-center justify-center transition-colors duration-150
                    ${agreed
                      ? "bg-primary border-primary text-white"
                      : "border-gray-300 dark:border-white/20 bg-white dark:bg-white/[0.05]"}`}
                >
                  {agreed && <CheckIcon />}
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  I have read and agree to the{" "}
                  <span className="text-primary font-semibold">Terms of Use</span>,{" "}
                  <span className="text-primary font-semibold">Privacy Policy</span>, and{" "}
                  <span className="text-primary font-semibold">End User Agreement</span>
                </span>
              </div>

              <div>
                <button
                  disabled={!agreed}
                  onClick={() => { if (agreed) window.location.href = "/resource-person-registration"; }}
                  className={`md:px-10 px-3 md:py-4 py-2 rounded-xl font-sans font-bold text-[15px] tracking-wide transition-all duration-200
                    ${agreed
                      ? "bg-primary text-white shadow-lg shadow-blue-500/25 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-500/35 cursor-pointer"
                      : "bg-gray-100 dark:bg-white/5 text-gray-300 dark:text-gray-600 cursor-not-allowed"}`}
                >
                  Proceed with Account Registration →
                </button>
              </div>

              <p className="text-sm text-gray-400 dark:text-gray-600 mt-4 font-sans">
                By continuing, you confirm you've read and understood all the above
              </p>
            </div>

          </>
        )}
      </main>
    </div>
  );
}